import { useEffect, useState } from "react";
import { Box, Container, Tab, Tabs, Typography, Avatar, Chip, Stack, Button } from "@mui/material";
import PropTypes from 'prop-types';
import { GetEvent } from "../../../../controllers/company/EventsController";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import { imagesURL } from "../../../../apiRoutes/apiBase";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
    faChartPie, faList, faTicket, faChartLine, 
    faGift, faCalendarDays, faLocationDot, faEye
} from "@fortawesome/free-solid-svg-icons";

// Componentes
import QUDProgress from "../../../../components/progress";
import CustomBreadcrumbs from "../../../../components/breadcrumbs";
import EventsSections from "../tickets/sections/index";
import IssueTicket from "../tickets/issue";
import TicketsSold from "../tickets/sold";
import IssueTicketWithSeats from "../tickets/issue/withSeats";
import TicketsRaffle from "../tickets/raffle";
import EventDashboard from "./EventDashboard";
import EventSections from "./EventSections";
import EventBoxOfficePOS from "./EventBoxOfficePOS";
import EventTicketsTab from "./EventTicketsTab";

function CustomTabPanel(props) {
    const { children, value, index, ...other } = props;
  
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
            <Box sx={{ py: 4 }}>
                {children}
            </Box>
            )}
        </div>
    );
}
  
CustomTabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
};

function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

export default function EventTabs(props) {
    // Inicializamos en 0 (que ahora será el Resumen)
    const [value, setValue] = useState(0);

    const [event, setEvent] = useState({});
    const [country, setCountry] = useState([]);
    const [exchangeRate, setExchangeRate] = useState(0);
    const [isLoadingData, setIsLoadingData] = useState(false);

    let { id } = useParams();

    const handleChangeTab = (event, newValue) => {
        setValue(newValue);
    };

    useEffect(() => {
        window.scrollTo(0,0);
        handleGetEvent();
    }, []);

    const handleGetEvent = async () => {
        setIsLoadingData(true);
        let response = await GetEvent(id);
        if(response.success === true) {
            setEvent(response.data.event);
            setExchangeRate(response.data.exchange_rate);
            setCountry(response.data.country);
        }
        setIsLoadingData(false)
    }

    return (
        <Container maxWidth='xl' sx={{ mb: 8 }}>
            <QUDProgress open={isLoadingData}/>
            
            <Box sx={{ mt: 3, mb: 2 }}>
                <CustomBreadcrumbs
                    returnTo='/events'
                    crumbs={[{name:'Eventos', link:'/events'}, {name: event?.nombre || 'Detalles del Evento', link:`/events/sections/${id}`}]}
                />
            </Box>
            
            {/* --- NUEVO HEADER (Dashboard Analítico) --- */}
            {event && Object.keys(event).length !== 0 && (
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, mt: 2, mb: 4, gap: 3 }}>
                    
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        {/* Miniatura del evento */}
                        <Avatar 
                            variant="rounded" 
                            src={event?.imagen ? `${imagesURL}/eventos/${event.imagen}` : ''} 
                            sx={{ width: 80, height: 80, borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }} 
                        />
                        
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5, flexWrap: 'wrap' }}>
                                <Typography variant="h4" fontWeight={800} color="#111">
                                    {event?.nombre || 'Cargando...'}
                                </Typography>
                                {/* Puedes conectar este Chip con el estatus real de tu BD */}
                                <Chip label="Activo" color="success" size="small" sx={{ fontWeight: 'bold', borderRadius: 1.5 }} />
                            </Box>
                            
                            <Stack direction="row" spacing={3} color="text.secondary" sx={{ mt: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                    <FontAwesomeIcon icon={faCalendarDays} size="sm" />
                                    <Typography variant="body2" fontWeight="500">
                                        {event?.fecha ? new Date(event.fecha).toLocaleDateString() : 'Fecha por definir'}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                    <FontAwesomeIcon icon={faLocationDot} size="sm" />
                                    <Typography variant="body2" fontWeight="500">
                                        {event?.ubicacion || 'Ubicación del evento'}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Box>
                    </Box>

                    {/* Botones de Acción Globales */}
                    <Box sx={{ display: 'flex', gap: 1.5, width: { xs: '100%', md: 'auto' } }}>
                        <Button 
                            variant="outlined" 
                            startIcon={<FontAwesomeIcon icon={faEye} />}
                            fullWidth sx={{ borderRadius: 2, fontWeight: 'bold', textTransform: 'none' }}
                        >
                            Ver Landing Page
                        </Button>
                    </Box>

                </Box>
            )}

            {/* --- SISTEMA DE PESTAÑAS --- */}
            <Box sx={{ width: '100%' }}>
                <Box sx={{ borderBottom: 1, borderColor: '#e0e0e0' }}>
                    <Tabs 
                        value={value} 
                        onChange={handleChangeTab} 
                        variant="scrollable" 
                        scrollButtons="auto" 
                        allowScrollButtonsMobile 
                        indicatorColor="primary"
                    >
                        {/* 0. NUEVA Pestaña de Resumen */}
                        <Tab icon={<FontAwesomeIcon icon={faChartPie} />} iconPosition="start" label={<Typography fontWeight="bold" variant="body2" textTransform="none">Resumen</Typography>} {...a11yProps(0)} />
                        {/* 1. Secciones */}
                        <Tab icon={<FontAwesomeIcon icon={faList} />} iconPosition="start" label={<Typography fontWeight="bold" variant="body2" textTransform="none">Secciones</Typography>} {...a11yProps(1)} />
                        {/* 2. Emitir Boletos */}
                        <Tab icon={<FontAwesomeIcon icon={faTicket} />} iconPosition="start" label={<Typography fontWeight="bold" variant="body2" textTransform="none">Emitir Boletos</Typography>} {...a11yProps(2)} />
                        {/* 3. Ventas */}
                        <Tab icon={<FontAwesomeIcon icon={faChartLine} />} iconPosition="start" label={<Typography fontWeight="bold" variant="body2" textTransform="none">Ventas</Typography>} {...a11yProps(3)} />
                        {/* 4. Sorteo */}
                        <Tab icon={<FontAwesomeIcon icon={faGift} />} iconPosition="start" label={<Typography fontWeight="bold" variant="body2" textTransform="none">Sorteo</Typography>} {...a11yProps(4)} />
                    </Tabs>
                </Box>

                {Object.keys(event).length !== 0 && (
                    <>
                        {/* --- 0: RESUMEN (Nuevo) --- */}
                        <CustomTabPanel value={value} index={0}>
                            <EventDashboard eventId={id} />
                        </CustomTabPanel>

                        {/* --- 1: SECCIONES --- */}
                        <CustomTabPanel value={value} index={1}>
                            <EventSections/>
                        </CustomTabPanel>

                        {/* --- 2: EMITIR BOLETOS --- */}
                        <CustomTabPanel value={value} index={2}>
                            <EventBoxOfficePOS event={event} exchangeRate={exchangeRate} />
                        </CustomTabPanel>

                        {/* --- 3: VENTAS --- */}
                        <CustomTabPanel value={value} index={3}>
                            <EventTicketsTab eventId={id}/>
                        </CustomTabPanel>
                        
                        {/* --- 4: SORTEO --- */}
                        <CustomTabPanel value={value} index={4}>
                            <TicketsRaffle />
                        </CustomTabPanel>
                    </>
                )}
            </Box>
        </Container>
    );
}